import { useSelector } from "react-redux";
import {
  Container,
  Row,
  Card,
  Col,
  Carousel,
  Placeholder,
} from "react-bootstrap";
import { RootState } from "../reducers";
import { Link } from "react-router-dom";
import useIsXS from "../hooks/useIsXS";
import Products from "./Products";
import ProductSkeletonCard from "./ProductSkeletonCard";
import useTenant from "../tenant/useTenant";
import { buildAssetUrl } from "../tenant/tenantConfig";
import { findActiveSaleBanner } from "../tenant/saleBanners";
import parse from "html-react-parser";

const chunkArray = (arr: any[], size: number) => {
  const chunked = [];
  for (let i = 0; i < arr.length; i += size) {
    chunked.push(arr.slice(i, i + size));
  }
  return chunked;
};

const Home = () => {
  const isXS = useIsXS();
  const tenant = useTenant();
  const home_categories = useSelector(
    (state: RootState) => state.shop.home_categories,
  );
  const isLoading = useSelector((state: RootState) => state.shop.isLoading);
  const textPages = useSelector((state: RootState) => state.shop.textPages);

  // Imágenes de portada provenientes del tenant config
  const HOME_IMAGE = buildAssetUrl(tenant.shopId, tenant.homeImageMobileFile);
  const HOME_IMAGE_WIDE = buildAssetUrl(tenant.shopId, tenant.homeImageWideFile);

  // Banner de la promo activa (Hot Sale / Cyber Monday) según el tenant config
  const activeSaleBanner = findActiveSaleBanner(tenant, home_categories);

  const renderSkeleton = () => (
    <>
      {Array.from({ length: 2 }).map((_, idx) => (
        <div key={idx} className="mb-4 mt-4">
          <Placeholder as="h3" animation="glow" className="mb-3 text-center">
            <Placeholder xs={isXS ? 6 : 3} />
          </Placeholder>
          <div
            className="d-flex justify-content-center px-4 pb-2"
            style={{ gap: "0.5rem" }}
          >
            {Array.from({ length: isXS ? 2 : 5 }).map((_, cardIdx) => (
              <div
                key={cardIdx}
                style={{ width: isXS ? "50%" : "20%", padding: "0 0.25rem" }}
              >
                <ProductSkeletonCard isXS={isXS} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderSalesImage = (image: string, link: string) => (
    <Link to={link} onClick={handleScrollToTop}>
      <Card border="light" style={{ maxWidth: tenant.homeImageWidth }}>
        <Card.Img src={image} />
      </Card>
    </Link>
  );

  return (
    <Container fluid>
      {textPages?.headerMessage && (
        <Row className="mb-3">
          <Col xs={12} className="text-center py-3">
            {parse(textPages.headerMessage)}
          </Col>
        </Row>
      )}
      {activeSaleBanner ? (
        isXS ? (
          activeSaleBanner.image && (
            <Row className="mb-3">
              <Col xs={12}>
                {renderSalesImage(activeSaleBanner.image, activeSaleBanner.link)}
              </Col>
            </Row>
          )
        ) : (
          activeSaleBanner.imageWide && (
            <Row className="mb-3">
              <Col xs={12} className="d-flex justify-content-center">
                {renderSalesImage(
                  activeSaleBanner.imageWide,
                  activeSaleBanner.link,
                )}
              </Col>
            </Row>
          )
        )
      ) : isXS ? (
        HOME_IMAGE && (
          <Row className="mb-3">
            <Col xs={12}>
              {renderSalesImage(HOME_IMAGE, "/product-type/weekly")}
            </Col>
          </Row>
        )
      ) : isXS ? (
        <Row className="mb-3">
          <Col xs={12}>
            {renderSalesImage(HOME_IMAGE, "/product-type/weekly")}
          </Col>
        </Row>
      ) : (
        HOME_IMAGE_WIDE && (
          <Row className="g-1 mb-1">
            <Col xs={12} className="d-flex justify-content-center">
              {renderSalesImage(HOME_IMAGE_WIDE, "/product-type/weekly")}
            </Col>
          </Row>
        )
      )}
      {!isLoading && home_categories?.length
        ? home_categories.map((category) => {
            if (!category.products || category.products.length === 0)
              return null;

            return (
              <div key={category.id_type} className="mb-4 mt-4">
                <h3 className="mb-3 text-center" style={{ fontWeight: 600 }}>
                  <Link
                    to={`/product-type/${category.id_type}`}
                    className="text-dark text-decoration-none"
                    onClick={handleScrollToTop}
                  >
                    {category.title}
                  </Link>
                </h3>
                <Carousel
                  indicators={false}
                  variant="dark"
                  interval={null}
                  className="product-carousel"
                >
                  {chunkArray(category.products, isXS ? 2 : 5).map(
                    (chunk, idx) => (
                      <Carousel.Item key={idx}>
                        <div
                          className="d-flex justify-content-center px-4 pb-2"
                          style={{ gap: "0.5rem" }}
                        >
                          {chunk.map((product) => (
                            <div
                              key={product.id}
                              style={{
                                width: isXS ? "50%" : "20%",
                                padding: "0 0.25rem",
                              }}
                            >
                              <Products product={product} />
                            </div>
                          ))}
                        </div>
                      </Carousel.Item>
                    ),
                  )}
                </Carousel>
                <div className="text-center mt-3 mb-5">
                  <Link
                    to={`/product-type/${category.id_type}`}
                    className="btn btn-outline-dark rounded-pill px-4 shadow-sm"
                    style={{ fontWeight: 500, fontSize: "0.9rem" }}
                    onClick={handleScrollToTop}
                  >
                    Ver todos en {category.title} &rarr;
                  </Link>
                </div>
              </div>
            );
          })
        : renderSkeleton()}
    </Container>
  );
};

export default Home;
